import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import API_URL from "../../api/API";
import { StaffSideMenu } from "../staff/StaffSideMenu";

export const AdminAthleteEditDynamicSpeedWorkout = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id, theme_id } = useParams();
  const navigate = useNavigate();

  const [dates, setDates] = useState([]);
  const [athleteName, setAthleteName] = useState("");
  const [existingWorkout, setExistingWorkout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchAthleteData = async () => {
      try {
        const response = await fetch(`${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch athlete details");
        const athleteData = await response.json();
        setAthleteName(athleteData.firstname + " " + athleteData.lastname);
      } catch (err) {
        setError(err.message);
      }
    };

    const fetchExistingWorkout = async () => {
      try {
        const response = await fetch(`${API_URL.GET_ATHLETE_DYNAMIC_WORKOUT_BY_THEME}${athlete_id}/${theme_id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${staff.token}`,
          },
        });
        
        if (!response.ok) throw new Error("Failed to fetch athlete's workout data");
        const workoutData = await response.json();
        setExistingWorkout(workoutData.theme);
        setDates([{ date: workoutData.date, themes: workoutData.theme ? [workoutData.theme] : [] }]);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAthleteData();
    fetchExistingWorkout();
  }, [athlete_id, staff.token, theme_id]);

  const handleAddSprint = (dateIndex, themeIndex) => {
    const updatedDates = [...dates];
    updatedDates[dateIndex].themes[themeIndex].sprints.push({
      sets: 0,
      distance: 0,
      reps: 0,
      rest: "",
      target_time: 0,
      notes: "",
      video_url: "",
    });
    setDates(updatedDates);
  };

  const handleInputChange = (e, field, dateIndex, themeIndex, sprintIndex) => {
    const updatedDates = [...dates];
    if (sprintIndex !== undefined) {
      updatedDates[dateIndex].themes[themeIndex].sprints[sprintIndex][field] = e.target.value;
    } else if (themeIndex !== undefined) {
      updatedDates[dateIndex].themes[themeIndex][field] = e.target.value;
    } else {
      updatedDates[dateIndex][field] = e.target.value;
    }
    setDates(updatedDates);
  };

  const handleDeleteSprint = (dateIndex, themeIndex, sprintIndex) => {
    const updatedDates = [...dates];
    updatedDates[dateIndex].themes[themeIndex].sprints = updatedDates[dateIndex].themes[
      themeIndex
    ].sprints.filter((_, index) => index !== sprintIndex);
    setDates(updatedDates);
  };

  const validateForm = () => {
    for (const dateEntry of dates) {
      for (const theme of dateEntry.themes) {
        if (!theme.title) return "Theme title is required";
        for (const sprint of theme.sprints) {
          if (sprint.sets <= 0) return "Sets must be greater than 0";
          if (sprint.distance <= 0) return "Distance must be greater than 0";
          if (sprint.reps <= 0) return "Reps must be greater than 0";
          if (sprint.target_time <= 0) return "Target time must be greater than 0";
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(API_URL.POST_SAVE_ATHLETE_WORKOUT_BY_THEME + athlete_id + "/" + theme_id, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ dates }),
      });

      if (!response.ok) {
        setError("Failed to save workout");
      } else {
        alert("Workout saved successfully!");
       // setDates([]);
        setError("");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // Months are 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <div className="flex h-screen">
    <StaffSideMenu />
    <div className="flex-grow flex flex-col p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          Edit Dynamic Speed Workout: <span className="text-green-600">{athleteName}</span>
        </h1>
        <button
          onClick={() => navigate(-1)}
          className="bg-red-500 text-white py-2 px-4 rounded"
        >
          Go Back
        </button>
      </div>

      {isLoading && <div className="bg-yellow-100 text-yellow-700 p-4 rounded">Loading...</div>}
      {error && <div className="bg-red-100 text-red-700 p-4 rounded">Error: {error}</div>}

      {dates.length > 0 && dates.map((dateEntry, dateIndex) => (
        <div key={dateIndex} className="p-4 my-4 rounded-lg">
          <div className="mb-4">
            <label className="block font-semibold">Date</label>
            <p>{formatDate(dateEntry.date)}</p>
          </div>
          {dateEntry.themes.map((theme, themeIndex) => (
            <div key={themeIndex} className="p-4 my-2 rounded-md shadow-md">
              <label className="block font-semibold">Theme Title</label>
              <input
                type="text"
                value={theme.title}
                className="border p-2 w-full mb-4"
                onChange={(e) => handleInputChange(e, "title", dateIndex, themeIndex)}
              />

              {theme.sprints.map((sprint, sprintIndex) => (
                <div key={sprintIndex} className="p-4 mb-2">
                  <div className="flex gap-4 mb-2">
                    {[
                      { label: "Sets", field: "sets" },
                      { label: "Distance", field: "distance" },
                      { label: "Reps", field: "reps" },
                      { label: "Target Time", field: "target_time" },
                    ].map(({ label, field }) => (
                      <div className="flex-1" key={field}>
                        <label className="block font-semibold">{label}</label>
                        <input
                          type="number"
                          value={sprint[field]}
                          className="border p-2 w-full"
                          onChange={(e) =>
                            handleInputChange(e, field, dateIndex, themeIndex, sprintIndex)
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-4 mb-2">
                    <div className="flex-1">
                      <label className="block font-semibold">Rest</label>
                      <textarea
                        value={sprint.rest}
                        className="border p-2 w-full"
                        onChange={(e) =>
                          handleInputChange(e, "rest", dateIndex, themeIndex, sprintIndex)
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block font-semibold">Notes</label>
                      <textarea
                        value={sprint.notes}
                        className="border p-2 w-full"
                        onChange={(e) =>
                          handleInputChange(e, "notes", dateIndex, themeIndex, sprintIndex)
                        }
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                        <label className="block font-semibold">Video Url</label>
                        <input
                          type="text"
                          className="border p-2 w-full rounded"
                          value={sprint.video_url}
                          onChange={(e) => handleInputChange(e, "video_url", dateIndex, themeIndex, sprintIndex)}
                                />
                  </div>


                  <div className="flex justify-end">
                    <button
                      onClick={() => handleDeleteSprint(dateIndex, themeIndex, sprintIndex)}
                      className="text-red-500 hover:underline"
                    >
                      Delete Sprint
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => handleAddSprint(dateIndex, themeIndex)}
                className="bg-blue-500 text-white py-2 px-4 rounded mt-4"
              >
                Add Sprint
              </button>
            </div>
          ))}
        </div>
      ))}
      <div className="flex justify-center mt-4">
        <button
          onClick={handleSave}
          className={`bg-green-500 text-white py-2 px-4 rounded ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Workout"}
        </button>
      </div>
    </div>
  </div>
  );
};
